import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Search, ShoppingBag, Filter } from 'lucide-react';
import MobileStickyBar from '../components/common/MobileStickyBar';
import CallButton from '../components/common/CallButton';
import SEO from '../components/common/SEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FAMILIES = [
  { id: '', label: 'Tout', icon: '🎪' },
  { id: 'jeux-gonflables',  label: 'Jeux gonflables',       icon: '🏰', categorie_nom: 'Jeux gonflables' },
  { id: 'photo',            label: 'Photo & Photobooth',     icon: '📷', categorie_nom: 'Photobooth' },
  { id: 'food',             label: 'Animations gourmandes',  icon: '🍬', categorie_nom: 'Gourmandises' },
  { id: 'boissons',         label: 'Boissons & Bar',         icon: '🍺', categorie_nom: 'Ambiance' },
  { id: 'jeux-bois',        label: 'Jeux en bois',           icon: '🎯', categorie_nom: 'Jeux en bois' },
  { id: 'mobilier',         label: 'Mobilier',               icon: '🪑', categorie_nom: 'Mobilier & Structures' },
  { id: 'deco',             label: 'Décoration',             icon: '🎨', categorie_nom: 'Décoration' },
  { id: 'son-lumiere',      label: 'Son & Lumière',          icon: '💡', categorie_nom: 'Son & lumière' },
];

const TYPE_FILTERS = [
  { id: '', label: 'Tous les types' },
  { id: 'prestation', label: 'Prestations' },
  { id: 'location', label: 'Location' },
  { id: 'vente', label: 'Vente' },
  { id: 'precommande', label: 'Précommande' },
  { id: 'fabrication', label: 'Sur-mesure' },
];

export default function CataloguePage() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFamily, setActiveFamily] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const { addItem } = useCart();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/products?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      // Filtre type : par défaut location uniquement, sauf si l'utilisateur choisit autre chose
      const type = typeFilter || 'location';
      url += `&product_type=${type}`;
      const family = FAMILIES.find(f => f.id === activeFamily);
      if (family?.categorie_nom) url += `&categorie_nom=${encodeURIComponent(family.categorie_nom)}`;
      const res = await fetch(url);
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }, [search, activeFamily, typeFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <SEO
        title="Catalogue location animations — Figeac, Lot (46)"
        description="Catalogue complet : photobooth, jeux gonflables, animations gourmandes, mobilier événementiel à Figeac. Livraison et installation. Devis gratuit."
        canonical="https://show-evenement.com/catalogue"
        breadcrumb={[{name: 'Catalogue', url: 'https://show-evenement.com/catalogue'}]}
      />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
        {/* Header */}
        <div className="mb-6">
          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-3" style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
            {total} prestation{total > 1 ? 's' : ''} disponible{total > 1 ? 's' : ''}
          </span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Catalogue complet
          </h1>
        </div>

        {/* Family filters — scrollable */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          {FAMILIES.map(f => (
            <button key={f.id} onClick={() => setActiveFamily(f.id)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all flex-shrink-0"
              style={{ background: activeFamily === f.id ? 'var(--teal)' : '#FFFFFF', color: activeFamily === f.id ? '#0B1E20' : 'var(--text-muted)', border: `1.5px solid ${activeFamily === f.id ? 'var(--teal)' : 'var(--border)'}`, boxShadow: activeFamily === f.id ? '0 4px 12px rgba(93,188,191,0.2)' : 'none' }}>
              <span>{f.icon}</span>
              {f.label}
            </button>
          ))}
        </div>

        {/* Search + type filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              data-testid="catalog-search-input"
              type="text"
              placeholder="Rechercher une animation..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm"
              style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {TYPE_FILTERS.map(f => (
              <button key={f.id} onClick={() => setTypeFilter(f.id)}
                className="px-3 py-2 rounded-xl text-xs font-medium transition-all"
                style={{ background: typeFilter === f.id ? 'var(--teal)' : '#FFFFFF', border: `1px solid ${typeFilter === f.id ? 'var(--teal)' : 'var(--border)'}`, color: typeFilter === f.id ? '#0B1E20' : 'var(--text-muted)' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag size={40} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun produit trouvé</p>
            <button onClick={() => { setSearch(''); setActiveFamily(''); setTypeFilter(''); }} className="btn-teal px-5 py-2 rounded-xl text-sm font-semibold mt-4">
              Voir tout le catalogue
            </button>
          </div>
        ) : (
          <div data-testid="catalog-results-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map(p => (
              <div key={p.id} className="card-premium overflow-hidden">
                <Link to={`/produit/${p.slug || p.id}`}>
                  <div className="relative overflow-hidden">
                    <img
                      src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&q=70'}
                      alt={p.nom}
                      className="w-full h-40 object-cover transition-transform duration-300 hover:scale-105"
                      loading="lazy"
                    />
                    {p.featured && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold"
                        style={{ background: 'var(--gold)', color: '#1A1A2E' }}>
                        ★ Best seller
                      </span>
                    )}
                  </div>
                </Link>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <Link to={`/produit/${p.slug || p.id}`}>
                      <h3 className="font-semibold text-xs leading-snug line-clamp-2" style={{ color: 'var(--ink)' }}>{p.nom}</h3>
                    </Link>
                  </div>
                  <span className="text-xs px-1.5 py-0.5 rounded-full mb-2 inline-block"
                    style={{ background: (p.type === 'location' || p.type === 'prestation') ? 'var(--teal-mist)' : 'rgba(212,175,55,0.1)', color: (p.type === 'location' || p.type === 'prestation') ? '#0B4B4E' : '#7A5E00', fontSize: '10px' }}>
                    {p.type === 'location' ? 'Location' : p.type === 'prestation' ? 'Prestation' : p.type === 'vente' ? 'Vente' : p.type === 'precommande' ? 'Précommande' : 'Sur-mesure'}
                  </span>
                  <div className="flex items-center justify-between mt-1">
                    {p.type === 'location' || p.type === 'fabrication' || p.type === 'prestation' ? (
                      <span className="text-sm font-bold price-gold">
                        {p.tarifs?.particulier > 0 ? `À partir de ${p.tarifs.particulier}€` : 'Nous contacter'}
                      </span>
                    ) : (
                      <span className="price-gold text-sm font-bold">{p.tarifs?.particulier || 0}€</span>
                    )}
                    <button onClick={() => { addItem(p); toast.success('Ajouté', { duration: 1500 }); }}
                      className="btn-teal px-2.5 py-1.5 text-xs font-semibold rounded-lg">
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <MobileStickyBar />
      <Footer />
    </div>
  );
}
